import { assignValue, type Customizer, enumerableStringKeysIn, isMergeable } from './objectCore';
import { keys } from './objectCollection';

function safeValue(object: Record<string, any>, key: string): any {
    const value = object[key];
    return key === '__proto__' || (key === 'constructor' && typeof value === 'function')
        ? undefined
        : value;
}

function isTypedArray(value: unknown): value is ArrayBufferView & ArrayLike<unknown> {
    return (
        ArrayBuffer.isView(value) &&
        Object.prototype.toString.call(value) !== '[object DataView]' &&
        'length' in value &&
        typeof value.length === 'number'
    );
}

function baseMerge(
    target: Record<string, any>,
    source: Record<string, any>,
    stack: Map<object, any>
): Record<string, any> {
    if (source === target) {
        return target;
    }
    const sourceKeys = new Set(keys(source));
    for (const key of enumerableStringKeysIn(source)) {
        sourceKeys.add(key);
    }
    for (const key of sourceKeys) {
        if (key === '__proto__') {
            continue;
        }
        const sourceValue = safeValue(source, key);
        const objectValue = safeValue(target, key);
        let nextValue = sourceValue;
        if (sourceValue !== null && typeof sourceValue === 'object') {
            if (stack.has(sourceValue)) {
                assignValue(target, key, stack.get(sourceValue));
                continue;
            }
            const tag = Object.prototype.toString.call(sourceValue);
            const typed = isTypedArray(sourceValue);
            const array = Array.isArray(sourceValue);
            let recurse = false;
            if (array || typed) {
                if (Array.isArray(objectValue)) {
                    nextValue = objectValue;
                    recurse = true;
                } else if (
                    objectValue != null &&
                    typeof objectValue !== 'function' &&
                    Number.isSafeInteger(objectValue.length) &&
                    objectValue.length >= 0
                ) {
                    nextValue = Array.from(objectValue as ArrayLike<unknown>);
                    recurse = true;
                } else if (typed) {
                    const Constructor = sourceValue.constructor as new (
                        buffer: ArrayBufferLike,
                        byteOffset: number,
                        length: number
                    ) => ArrayBufferView;
                    nextValue = new Constructor(
                        sourceValue.buffer.slice(0),
                        sourceValue.byteOffset,
                        sourceValue.length
                    );
                } else {
                    nextValue = [];
                    recurse = true;
                }
            } else if (isMergeable(sourceValue) || tag === '[object Arguments]') {
                nextValue =
                    objectValue !== null && typeof objectValue === 'object' ? objectValue : {};
                if (Object.prototype.toString.call(nextValue) === '[object Arguments]') {
                    nextValue = { ...nextValue };
                }
                recurse = true;
            }
            if (recurse) {
                stack.set(sourceValue, nextValue);
                baseMerge(nextValue, sourceValue, stack);
                stack.delete(sourceValue);
            }
        }
        if (nextValue !== undefined || !(key in target)) {
            assignValue(target, key, nextValue);
        }
    }
    return target;
}

export function assignWith(object: any, ...sourcesAndCustomizer: any[]): any {
    const possibleCustomizer = sourcesAndCustomizer[sourcesAndCustomizer.length - 1];
    const customizer =
        typeof possibleCustomizer === 'function'
            ? (sourcesAndCustomizer.pop() as Customizer)
            : undefined;
    const target = Object(object) as Record<PropertyKey, any>;
    for (const source of sourcesAndCustomizer) {
        if (source == null) {
            continue;
        }
        for (const key of Object.keys(Object(source))) {
            const sourceRecord = source as Record<string, any>;
            const customized = customizer?.(
                target[key],
                sourceRecord[key],
                key,
                target,
                sourceRecord
            );
            assignValue(target, key, customized === undefined ? sourceRecord[key] : customized);
        }
    }
    return target;
}

export function merge(object: any, ...sources: any[]): any {
    const target = Object(object) as Record<string, any>;
    for (const source of sources) {
        if (source != null) {
            baseMerge(target, Object(source), new Map());
        }
    }
    return target;
}
